// Catalogs of supported language toolchains and backing services for
// the yml profile. Curated whitelists keep the surface small and
// reviewable; unknown values are rejected up front rather than passed
// through to devcontainer / compose.

import { readFileSync } from 'node:fs';
import { fileURLToPath } from 'node:url';
import type { ServiceHealthcheck, ServiceObject } from '../config/schema.js';
import type { ResolvedService } from './types.js';
import { loadDescriptorCatalogSync } from '../catalog/load-sync.js';
import { serviceProxyAlias } from '../config/http-services.js';
import type { CatalogComponent } from '../catalog/load.js';
import type { BriefingLine } from '../catalog/descriptor.js';

// Monoceros runtime image — thin layer on top of Microsoft's
// typescript-node base (see images/runtime/Dockerfile). The default
// points at the floating major tag on GHCR, so an `apply` from a
// fresh install pulls a published image without further setup.
//
// Contributors who are iterating on the runtime image itself
// (`pnpm image:build` → `monoceros-runtime:dev`) can override this
// via the `MONOCEROS_BASE_IMAGE_OVERRIDE` env var to point at their
// local tag without editing source. Empty or whitespace-only values
// are ignored so an accidentally-set-blank var doesn't break apply.
const DEFAULT_BASE_IMAGE = 'ghcr.io/getmonoceros/monoceros-runtime:1';
const override = process.env.MONOCEROS_BASE_IMAGE_OVERRIDE?.trim();
export const BASE_IMAGE =
  override && override.length > 0 ? override : DEFAULT_BASE_IMAGE;

// Registry repo for the Monoceros runtime image. The yml pins only a
// version (ADR 0017); the repo is a CLI constant.
const RUNTIME_IMAGE_REPO = 'ghcr.io/getmonoceros/monoceros-runtime';

// The runtime version a fresh `monoceros init` pins. SINGLE source of
// truth: `images/runtime/VERSION` (the same file release-runtime.yml
// publishes from). tsup substitutes the literal at build time (see
// tsup.config.ts → `__DEFAULT_RUNTIME_VERSION__`); in dev/test (tsx, no
// substitution) we read the file from the repo. So a runtime-image bump
// is a ONE-LINE change in `images/runtime/VERSION` — this is never
// hand-edited, and `init`'s pin can't drift from the published image.
declare const __DEFAULT_RUNTIME_VERSION__: string;
export const DEFAULT_RUNTIME_VERSION: string =
  typeof __DEFAULT_RUNTIME_VERSION__ === 'string'
    ? __DEFAULT_RUNTIME_VERSION__
    : readFileSync(
        fileURLToPath(
          new URL('../../../../images/runtime/VERSION', import.meta.url),
        ),
        'utf8',
      ).trim();

// Minimum runtime version that ships the node-owned `~/.vscode-server`
// dirs the IDE-state volumes need (ADR 0015). Below this, the scaffold
// must not emit those volume mounts or the container breaks on start.
// Unlike DEFAULT_RUNTIME_VERSION this is a deliberate, hand-set
// per-feature gate: it's the version the capability was INTRODUCED in
// and stays frozen there even as the image moves on. A future
// image-gated feature gets its own MIN_RUNTIME_FOR_<feature>.
const MIN_RUNTIME_FOR_IDE_VOLUMES = '1.1.0';

// Minimum runtime version that ships sshd + socat for the universal IDE
// attach point (ADR 0022). Below this the image has no sshd, so the
// host-side SSH config would point at a dead port - apply skips the
// whole SSH setup. Frozen at the version the capability was INTRODUCED
// in, like MIN_RUNTIME_FOR_IDE_VOLUMES above.
const MIN_RUNTIME_FOR_SSH_ATTACH = '1.2.0';

// Minimum runtime that persists SSH host keys across rebuilds + ships a
// WORKING Windows ssh bridge forwarder (ADR 0022 revision). 1.3.4 shipped the
// forwarder but read its port from an env var that `sudo` stripped, so it
// never started - the direct-port block was dead. 1.3.5 reads the port as an
// argument. At/above it, `apply` records the container's (stable) host key in
// `~/.ssh/known_hosts` (the Claude desktop app's ssh2 requires it - no
// trust-on-first-use) and on Windows publishes a host-loopback port for a
// direct (no-ProxyCommand) connection. Below it, none of that is emitted
// (Windows keeps the ProxyCommand block, which works for system OpenSSH).
const MIN_RUNTIME_FOR_HOST_KEY_PINNING = '1.3.5';

// Minimum runtime whose entrypoint brings up sshd on every container start
// (issue #20). Below this, sshd came up only from the devcontainer
// postStartCommand, which a plain `docker restart` / Docker Desktop restart /
// host reboot does NOT re-run - so sshd stayed down until `monoceros start`.
// At/above it the image entrypoint runs `monoceros-sshd-up.sh`, and the
// scaffold sets `overrideCommand: false` in image mode so that entrypoint
// actually runs as PID 1 (devcontainer-cli otherwise replaces it with its own
// keep-alive). Frozen at the introducing version.
const MIN_RUNTIME_FOR_ENTRYPOINT_SSHD = '1.3.6';

// Minimum runtime that ships the always-on browser-bridge plumbing: a
// permanent relay `xdg-open` on PATH plus `BROWSER` pointing at it, so a
// tool in ANY session (including one spawned by an IDE/desktop-app SSH
// attach, not just `monoceros run`/`shell`) can open the host browser via
// the host-side bridge daemon. Below this the image has neither, so apply
// skips spawning the daemon. Frozen at the introducing version.
const MIN_RUNTIME_FOR_BROWSER_BRIDGE = '1.3.3';

// Minimum runtime whose `monoceros-ctl` carries the `reconcile` subcommand
// (ADR 0028): it restarts every "wanted" app target (one started and not
// cleanly stopped) that is currently down. Below this the image's runner has
// no `reconcile`, so apply does not try to restore running apps after a
// bring-up. Frozen at the introducing version.
const MIN_RUNTIME_FOR_APP_RESTART = '1.6.0';

// Minimum runtime whose `monoceros-ctl list` understands `--json` (NDJSON,
// one object per target). Gates the Apps section of `monoceros status`: below
// this the runner has no machine-readable surface, so status omits per-app run
// state. Same image as the `reconcile` work; frozen at the introducing version.
const MIN_RUNTIME_FOR_APP_STATUS = '1.6.0';

// Minimum runtime whose `monoceros-ctl start` honours a target's
// `readyTimeout`. Below this the field is silently ignored and every target
// gets the built-in 20s window - which is what `monoceros check` warns about,
// because a compiled server then keeps being reported as failed while it is
// still building. Frozen at the introducing version.
const MIN_RUNTIME_FOR_READY_TIMEOUT = '1.6.2';

/**
 * Resolve a pinned `runtimeVersion` to a concrete image ref.
 * `MONOCEROS_BASE_IMAGE_OVERRIDE` (dev) always wins. With no pin we fall
 * back to the legacy floating major tag (pre-0017 behavior) so an
 * unpinned yml still yields a usable — if non-reproducible — image;
 * `apply` is what rejects an unpinned yml. See ADR 0017.
 */
export function resolveRuntimeImage(version?: string): string {
  const ov = process.env.MONOCEROS_BASE_IMAGE_OVERRIDE?.trim();
  if (ov && ov.length > 0) return ov;
  if (!version) return `${RUNTIME_IMAGE_REPO}:1`;
  return `${RUNTIME_IMAGE_REPO}:${version}`;
}

/** Compare two exact `major.minor.patch` versions: -1 | 0 | 1. */
export function compareRuntimeVersions(a: string, b: string): number {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < 3; i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d < 0 ? -1 : 1;
  }
  return 0;
}

/**
 * Whether the pinned runtime supports the ADR-0015 IDE-state volumes.
 * False when unpinned (legacy) or below the minimum — those containers
 * get no IDE volume mounts, so they never break on an image lacking the
 * node-owned `~/.vscode-server` dirs.
 */
export function runtimeSupportsIdeVolumes(version?: string): boolean {
  if (!version) return false;
  return compareRuntimeVersions(version, MIN_RUNTIME_FOR_IDE_VOLUMES) >= 0;
}

/**
 * Whether the pinned runtime ships sshd + socat for the ADR-0022 IDE
 * attach point. False when unpinned or below the minimum - those
 * containers get no host-side SSH config (the image has no sshd).
 */
export function runtimeSupportsSshAttach(version?: string): boolean {
  if (!version) return false;
  return compareRuntimeVersions(version, MIN_RUNTIME_FOR_SSH_ATTACH) >= 0;
}

/**
 * Whether the pinned runtime ships the always-on browser-bridge relay
 * (`xdg-open` on PATH + `BROWSER`). False when unpinned or below the
 * minimum - those containers have no relay, so apply does not spawn the
 * host-side bridge daemon (it would watch a file nothing ever writes).
 */
export function runtimeSupportsBrowserBridge(version?: string): boolean {
  if (!version) return false;
  return compareRuntimeVersions(version, MIN_RUNTIME_FOR_BROWSER_BRIDGE) >= 0;
}

/**
 * Whether the pinned runtime persists host keys + ships the Windows ssh
 * bridge forwarder (ADR 0022 revision). Gates `apply`'s `known_hosts` write
 * and the Windows host-loopback port publish. False when unpinned or below
 * the minimum.
 */
export function runtimeSupportsHostKeyPinning(version?: string): boolean {
  if (!version) return false;
  return compareRuntimeVersions(version, MIN_RUNTIME_FOR_HOST_KEY_PINNING) >= 0;
}

/**
 * Whether the pinned runtime's entrypoint brings up sshd on every container
 * start (issue #20). Gates the image-mode `overrideCommand: false`: only at/
 * above this version does the image have both the entrypoint sshd bring-up and
 * a baked keep-alive CMD, so turning off devcontainer-cli's command override
 * is safe (the entrypoint becomes PID 1 and keeps the container alive). False
 * when unpinned or below the minimum - those containers keep the default
 * override and recover sshd only via `monoceros start`.
 */
export function runtimeSupportsEntrypointSshd(version?: string): boolean {
  if (!version) return false;
  return compareRuntimeVersions(version, MIN_RUNTIME_FOR_ENTRYPOINT_SSHD) >= 0;
}

/**
 * Whether the pinned runtime's `monoceros-ctl` ships the `reconcile`
 * subcommand (ADR 0028). Gates apply's post-bring-up "restore running apps"
 * step: only at/above this version can the runner restart the targets that
 * were up before the recreate. False when unpinned or below the minimum -
 * those containers simply come back with apps stopped (the prior behavior).
 */
export function runtimeSupportsAppRestart(version?: string): boolean {
  if (!version) return false;
  return compareRuntimeVersions(version, MIN_RUNTIME_FOR_APP_RESTART) >= 0;
}

/**
 * Whether the pinned runtime's `monoceros-ctl list` supports `--json`. Gates
 * the per-app run-state (Apps section) of `monoceros status`. False when
 * unpinned or below the minimum - status still renders the container, services,
 * ports and built-in sections, just without live app state.
 */
export function runtimeSupportsAppStatus(version?: string): boolean {
  if (!version) return false;
  return compareRuntimeVersions(version, MIN_RUNTIME_FOR_APP_STATUS) >= 0;
}

/**
 * Whether the pinned runtime's `monoceros-ctl start` honours a target's
 * `readyTimeout`. Below this the runner does not know the field and every
 * target keeps the built-in 20s readiness window. False when unpinned too -
 * support is then unknowable from the yml, so a caller that turns this into a
 * warning must skip the unpinned case rather than cry wolf.
 */
export function runtimeSupportsReadyTimeout(version?: string): boolean {
  if (!version) return false;
  return compareRuntimeVersions(version, MIN_RUNTIME_FOR_READY_TIMEOUT) >= 0;
}

export interface LanguageEntry {
  id: string;
  /** Product name for the yml header, e.g. `Node.js` for `node`. */
  displayName: string;
  /** One-or-two-sentence descriptor prose for the yml header. */
  description: string;
  feature: string;
  /**
   * Feature options always applied for this language, on top of which
   * the optional `version` from the spec is layered. Lets a language
   * ship sensible defaults its upstream feature leaves off — e.g. the
   * `java` feature installs only a JDK by default, so we turn on
   * Maven + Gradle here so a plain `languages: [java]` is build-ready.
   */
  defaultOptions?: Readonly<Record<string, unknown>>;
  /**
   * The subset of options the builder should SEE in the yml (descriptor
   * `surface: yml`). `init` renders these as the language's object form
   * (e.g. java's `installMaven` / `installGradle`); editing them in the yml
   * overrides the default at apply time. A superset-or-equal of these always
   * applies via `defaultOptions` regardless of what the yml shows.
   */
  ymlOptions?: Readonly<Record<string, string | number | boolean>>;
  /**
   * Version rendered inline in the yml (`name:<defaultVersion>`) so the
   * builder sees where to edit it. For a builtin language this is the
   * base-image version: pinning exactly it stays builtin (no feature install
   * — see `resolveFeatures`); a different version triggers the feature.
   */
  defaultVersion?: string;
  /**
   * VS Code extensions to recommend in the `.code-workspace` when this
   * language is present (ADR 0016). See descriptor.ts for the cross-editor
   * (VS Code / Codium) list-both rationale.
   */
  vscodeExtensions?: readonly string[];
  /**
   * Home-relative dirs persisted per container across a rebuild (what the
   * PROJECT installs, e.g. Go's `GOBIN`). See descriptor.ts for why this is
   * per container while `sharedCachePaths` is machine-wide.
   */
  persistentHomePaths?: readonly string[];
  /**
   * Absolute in-container dirs backed by a machine-wide volume shared by every
   * workbench with this language — compiler caches, downloaded and compiled
   * once. See descriptor.ts.
   */
  sharedCachePaths?: readonly string[];
  /** Literal env injected into the workspace container for this language. */
  workspaceEnv?: Readonly<Record<string, string>>;
}

// ─── Descriptor-derived catalogs (ADR 0020) ──────────────────────
// LANGUAGE_CATALOG / SERVICE_CATALOG / BUILTIN_LANGUAGES are no longer
// hand-written literals: they are a typed projection of the unified
// component descriptors under `components/`, loaded synchronously at import
// so these eager const exports keep their original shape and every existing
// consumer keeps working unchanged. The descriptor is the single source of
// truth (see ADR 0020); these records derive from it.
const DESCRIPTORS = loadDescriptorCatalogSync();

/** Plain `{ key: defaultValue }` for the options that declare a default. */
function descriptorOptionDefaults(
  options: Record<string, { default?: string | boolean | number }>,
): Record<string, string | boolean | number> {
  const out: Record<string, string | boolean | number> = {};
  for (const [key, spec] of Object.entries(options)) {
    if (spec.default !== undefined) out[key] = spec.default;
  }
  return out;
}

/** Option defaults the descriptor marks `surface: yml` (shown in the yml). */
function descriptorYmlOptionDefaults(
  options: Record<
    string,
    { default?: string | boolean | number; surface?: string }
  >,
): Record<string, string | boolean | number> {
  const out: Record<string, string | boolean | number> = {};
  for (const [key, spec] of Object.entries(options)) {
    if (spec.surface === 'yml' && spec.default !== undefined) {
      out[key] = spec.default;
    }
  }
  return out;
}

/** CLI/yml selector for a component (its `name`, defaulting to `id`). */
function descriptorSelector(c: CatalogComponent): string {
  return c.descriptor.name ?? c.descriptor.id;
}

// `node` is in the base runtime image (descriptor `language.builtin: true`),
// so the bare entry `languages: [node]` installs nothing extra. Versioned
// node — `node:20` — bypasses the builtin set and goes through the upstream
// feature, because the base image's node version isn't otherwise selectable.
export const BUILTIN_LANGUAGES = new Set<string>(
  [...DESCRIPTORS.values()]
    .filter((c) => c.category === 'language' && c.descriptor.language?.builtin)
    .map(descriptorSelector),
);

export const LANGUAGE_CATALOG: Readonly<Record<string, LanguageEntry>> =
  Object.fromEntries(
    [...DESCRIPTORS.values()]
      .filter((c) => c.category === 'language')
      .map((c) => {
        const key = descriptorSelector(c);
        const defaults = descriptorOptionDefaults(c.descriptor.options);
        const ymlOptions = descriptorYmlOptionDefaults(c.descriptor.options);
        const entry: LanguageEntry = {
          id: key,
          displayName: c.descriptor.displayName,
          description: c.descriptor.description,
          feature: c.descriptor.language!.feature,
          ...(Object.keys(defaults).length > 0
            ? { defaultOptions: defaults }
            : {}),
          ...(Object.keys(ymlOptions).length > 0 ? { ymlOptions } : {}),
          ...(c.descriptor.language!.defaultVersion !== undefined
            ? { defaultVersion: c.descriptor.language!.defaultVersion }
            : {}),
          ...(c.descriptor.language!.vscodeExtensions
            ? { vscodeExtensions: c.descriptor.language!.vscodeExtensions }
            : {}),
          ...(c.descriptor.language!.persistentHomePaths
            ? {
                persistentHomePaths: c.descriptor.language!.persistentHomePaths,
              }
            : {}),
          ...(c.descriptor.language!.sharedCachePaths
            ? { sharedCachePaths: c.descriptor.language!.sharedCachePaths }
            : {}),
          ...(c.descriptor.language!.workspaceEnv
            ? { workspaceEnv: c.descriptor.language!.workspaceEnv }
            : {}),
        };
        return [key, entry];
      }),
  );

/**
 * Language entries in a container yml may carry an optional
 * version suffix: `java:17`, `node:20`. The suffix is anything
 * the upstream devcontainer feature accepts as its `version`
 * option (typically `latest`, a major like `17`, or an exact
 * semver like `3.12.1`).
 */
export const LANGUAGE_SPEC_RE = /^([a-z][a-z0-9-]*)(?::([A-Za-z0-9._-]+))?$/;

export interface LanguageSpec {
  name: string;
  version?: string;
}

/**
 * Split a yml language entry into name + optional version. Returns
 * `null` when the input is not a valid language spec. Callers use
 * that null to surface a schema error.
 */
export function parseLanguageSpec(spec: string): LanguageSpec | null {
  const m = LANGUAGE_SPEC_RE.exec(spec);
  if (!m) return null;
  return { name: m[1]!, ...(m[2] !== undefined ? { version: m[2] } : {}) };
}

export interface ServiceEntry {
  id: string;
  /** Product name for the yml header, e.g. `PostgreSQL` for `postgres`. */
  displayName: string;
  /** One-or-two-sentence descriptor prose for the yml header. */
  description: string;
  image: string;
  /**
   * Literal dev-default values for the service's env vars. These are
   * rendered as `${KEY}` *placeholders* into the yml (expandCuratedService)
   * and seeded as `KEY=<default>` into `<name>.env` (curatedServiceEnvDefaults),
   * so the yml is shareable without baking credentials in while the
   * connection string stays predictable out of the box.
   */
  env?: Readonly<Record<string, string>>;
  /**
   * Readiness probe. Curated services ship one so the workspace's
   * `depends_on` gates on `service_healthy` (actually accepting
   * connections) rather than just `service_started`. `${VAR}` in the
   * test resolves from `<name>.env` at apply time like any other field.
   */
  healthcheck?: ServiceHealthcheck;
  /**
   * Container-side mount target for the service's persistent data.
   * Monoceros bind-mounts this onto `<container-dir>/data/<id>/` on
   * the host so DB content is visible in the host filesystem
   * (browsable, backupable, removable with the usual tools instead
   * of `docker volume ...`). See ADR 0003 for the per-container
   * state-model the data dir slots into.
   */
  dataMount?: string;
  /**
   * Compose `user:` for the service container (e.g. `"0:0"`). Set for
   * images that run as a fixed non-root uid but must write a host
   * bind-mounted `dataMount` — without it they can't write the
   * apply-created data dir on native Linux and exit. See descriptor.ts.
   */
  user?: string;
  /**
   * Default in-container port the service listens on. Used by
   * `monoceros tunnel <name> <service>` to resolve the service-name
   * to a port without an extra CLI argument. See ADR 0009.
   */
  defaultPort: number;
  /**
   * The service's HTTP port that may leave the container: `monoceros share`
   * offers it on the LAN, the proxy routes to it. Absent means neither, which
   * is why a database is never shared. Differs from `defaultPort` wherever the
   * machine port is not the human one (mailpit 1025 vs 8025, rustfs 9000 vs
   * 9001). See descriptor.ts.
   */
  httpPort?: number;
  /**
   * VS Code extensions to *recommend* (not auto-install) when this
   * service is present. Written to `extensions.recommendations` in the
   * generated `.code-workspace`. Unlike feature-bound extensions (which
   * auto-install via the feature manifest), these are soft, context-
   * derived suggestions the builder can dismiss. See ADR 0016.
   */
  vscodeExtensions?: readonly string[];
  /**
   * Workspace connection env, keyed by logical SUFFIX (`URL`/`HOST`/`PORT`/
   * `USER`/`PASSWORD`/`DB`) → template. Baked into the yml at expand and
   * emitted as `<UPPER(name)>_<SUFFIX>` per instance by `serviceConnectionEnv`
   * (ADR 0021). Tokens `${host}`/`${port}`/`${<OPTION>}` are filled there.
   */
  connectionEnv?: Readonly<Record<string, string>>;
  /**
   * CLI client tool(s) installed into the WORKSPACE when this service is
   * present (ADR 0020). `apt` packages merge into the workspace apt-packages
   * feature (build-time); `npm` packages are installed globally in post-create
   * (guarded). E.g. postgres → apt `postgresql-client`, mongodb → npm
   * `mongosh`.
   */
  client?: Readonly<{ apt?: readonly string[]; npm?: readonly string[] }>;
  /**
   * Compose `command:` baked into the expanded yml (visible + editable).
   * The process to run instead of the image's default CMD, e.g. Keycloak's
   * `start-dev --import-realm`. See descriptor.ts.
   */
  command?: string;
  /**
   * Example bind-mounts rendered as a COMMENTED `volumes:` scaffold in the
   * generated yml (not active volumes). For services that need a project
   * file the catalog can't path-resolve, e.g. Keycloak's realm.json.
   * See descriptor.ts.
   */
  exampleVolumes?: readonly string[];
  /**
   * Start this service in a host-side SECOND WAVE, after `devcontainer up`
   * (and the in-container clone) has finished, rather than together with
   * the workspace. For services that bind-mount a file from a cloned repo
   * (Keycloak realm.json, …). Hidden / descriptor-only: not a yml field,
   * resolved here by name via `serviceDefersStart`. See ADR 0025.
   */
  deferStart?: boolean;
  /**
   * Agent-facing guidance lines from the descriptor's `briefing:`, rendered
   * under the service in the generated AGENTS.md (see briefing/agents-md.ts).
   * The single source for what the in-container agent is told about a service.
   */
  briefing?: readonly BriefingLine[];
  /**
   * Pipeline-shaped compose body from the descriptor's `deploy.compose`,
   * rendered under this service in the generated `.monoceros/deploy.md`
   * (see briefing/deploy-md.ts). Carries the catalog's `image:`; the
   * renderer swaps in the image this container actually runs.
   */
  deploy?: string;
  /**
   * Compose fragment with top-level keys for what this service needs beside
   * itself (descriptor `deploy.requires`), e.g. Keycloak's own database plus
   * that volume's declaration. Rendered verbatim after the service's own
   * block in `.monoceros/deploy.md`.
   */
  deployRequires?: string;
  /**
   * File names in the component's `tools/` dir, copied at apply into
   * `<container>/.monoceros/bin/` (see scaffold.ts#writeServiceTools).
   */
  tools?: readonly string[];
}

// The `monoceros` user/password/db below are deliberate dev-only
// defaults, not secrets. A curated service renders its env as `${KEY}`
// placeholders into the yml and seeds these literals into the gitignored
// `<name>.env`, so the yml stays shareable while the connection string
// is predictable out of the box — any builder running this workbench
// knows it at a glance:
//
//   postgresql://monoceros:monoceros@postgres:5432/monoceros
//   mysql://monoceros:monoceros@mysql:3306/monoceros
//
// To use a real password, change the value in `<name>.env` (it never
// leaves the host, never rides along when the yml is shared). Because
// the default isn't a secret, the secret-masking layer
// (util/mask-secrets.ts) doesn't and shouldn't mask it.
export const SERVICE_CATALOG: Readonly<Record<string, ServiceEntry>> =
  Object.fromEntries(
    [...DESCRIPTORS.values()]
      .filter((c) => c.category === 'service')
      .map((c) => {
        const key = descriptorSelector(c);
        const svc = c.descriptor.service!;
        if (svc.defaultPort === undefined) {
          throw new Error(
            `Service descriptor '${key}' is missing service.defaultPort.`,
          );
        }
        // env defaults are modeled as the service's surface:env options.
        const env = descriptorOptionDefaults(c.descriptor.options) as Record<
          string,
          string
        >;
        const entry: ServiceEntry = {
          id: key,
          image: svc.image,
          ...(Object.keys(env).length > 0 ? { env } : {}),
          ...(svc.healthcheck
            ? { healthcheck: svc.healthcheck as ServiceHealthcheck }
            : {}),
          ...(svc.dataMount ? { dataMount: svc.dataMount } : {}),
          ...(svc.user ? { user: svc.user } : {}),
          displayName: c.descriptor.displayName,
          description: c.descriptor.description,
          defaultPort: svc.defaultPort,
          ...(svc.httpPort !== undefined ? { httpPort: svc.httpPort } : {}),
          ...(svc.vscodeExtensions
            ? { vscodeExtensions: svc.vscodeExtensions }
            : {}),
          ...(svc.connectionEnv ? { connectionEnv: svc.connectionEnv } : {}),
          ...(svc.client ? { client: svc.client } : {}),
          ...(svc.command ? { command: svc.command } : {}),
          ...(svc.exampleVolumes ? { exampleVolumes: svc.exampleVolumes } : {}),
          ...(svc.deferStart ? { deferStart: true } : {}),
          ...(c.descriptor.briefing && c.descriptor.briefing.length > 0
            ? { briefing: c.descriptor.briefing }
            : {}),
          ...(c.descriptor.deploy
            ? { deploy: c.descriptor.deploy.compose }
            : {}),
          ...(c.descriptor.deploy?.requires
            ? { deployRequires: c.descriptor.deploy.requires }
            : {}),
          ...(svc.tools ? { tools: svc.tools } : {}),
        };
        return [key, entry];
      }),
  );

export function knownLanguages(): string[] {
  return [...BUILTIN_LANGUAGES, ...Object.keys(LANGUAGE_CATALOG)].sort();
}

export function knownServices(): string[] {
  return Object.keys(SERVICE_CATALOG).sort();
}

/**
 * Compose profile assigned to deferred services so `devcontainer up`'s
 * profile-less `docker compose up -d` does NOT start them (a profiled
 * service is skipped unless its profile is active). The second wave brings
 * them up with `--profile <this>`. This is the mechanism that actually
 * holds back the first-wave start — `runServices` does not, because
 * devcontainer-cli's compose `up` ignores it for which services boot.
 * See ADR 0025.
 */
export const DEFERRED_SERVICE_PROFILE = 'monoceros-deferred';

/**
 * Whether a service starts in the host-side second wave (after the
 * in-container clone) rather than together with the workspace. Resolved
 * by catalog name — `deferStart` is a hidden, descriptor-only attribute,
 * so a renamed instance (yml `name` ≠ catalog id) is treated as
 * non-deferred. See ADR 0025.
 */
export function serviceDefersStart(name: string): boolean {
  return SERVICE_CATALOG[name]?.deferStart === true;
}

/**
 * Example bind-mounts for a curated service, rendered as a commented
 * `volumes:` scaffold in the generated yml (see init/service-doc.ts).
 * Empty for services without any. Looked up by catalog name.
 */
export function curatedServiceExampleVolumes(name: string): readonly string[] {
  return SERVICE_CATALOG[name]?.exampleVolumes ?? [];
}

/**
 * Product name and prose of a curated language, for the header above its entry.
 * Undefined for a name the catalog does not carry.
 */
export function curatedLanguageHeader(
  name: string,
): { displayName: string; description: string } | undefined {
  const entry = LANGUAGE_CATALOG[name];
  if (!entry) return undefined;
  return { displayName: entry.displayName, description: entry.description };
}

/**
 * Product name and prose of a curated service, for the header above its block.
 * Undefined for a custom image, which has neither.
 */
export function curatedServiceHeader(
  name: string,
): { displayName: string; description: string } | undefined {
  const entry = SERVICE_CATALOG[name];
  if (!entry) return undefined;
  return { displayName: entry.displayName, description: entry.description };
}

/**
 * Agent-facing briefing lines for a curated service, from its descriptor's
 * `briefing:` field. Rendered under the service in AGENTS.md. Empty for
 * services (or non-curated names) without one. Service briefing lines are
 * unconditional - `whenOption` gating is a feature-only concern.
 */
export function curatedServiceBriefing(name: string): readonly string[] {
  return (SERVICE_CATALOG[name]?.briefing ?? []).map((line) => line.text);
}

/**
 * Pipeline compose body for a curated service, from its descriptor's
 * `deploy.compose`. Undefined for a service without one and for
 * non-curated (custom-image) names — Monoceros knows nothing about those,
 * and `.monoceros/deploy.md` says so rather than guessing.
 */
/**
 * Tool file names a curated service contributes to the workspace. Looked up
 * by catalog name, like `client` and `deploy`: a renamed instance
 * (`--as analytics`) gets none, because its connection env is prefixed with
 * the new name and the tool reads the catalog-named one.
 */
export function curatedServiceTools(name: string): readonly string[] {
  return SERVICE_CATALOG[name]?.tools ?? [];
}

export function curatedServiceDeploy(name: string): string | undefined {
  return SERVICE_CATALOG[name]?.deploy;
}

/**
 * Compose fragment for what a curated service needs beside itself
 * (`deploy.requires`), e.g. Keycloak's own database. Undefined when the
 * service needs nothing extra.
 */
export function curatedServiceDeployRequires(name: string): string | undefined {
  return SERVICE_CATALOG[name]?.deployRequires;
}

/**
 * Normalize a `services:` object to a `ResolvedService` — fills the two
 * fields the scaffold treats as always-present (env, volumes) with their
 * empty defaults. `${VAR}` references in env/command pass through
 * untouched; they're resolved against `<name>.env` at apply time
 * (config/env-file.ts).
 */
export function resolveService(entry: ServiceObject): ResolvedService {
  return {
    name: entry.name,
    image: entry.image,
    ...(entry.port !== undefined ? { port: entry.port } : {}),
    ...(entry.httpPort !== undefined ? { httpPort: entry.httpPort } : {}),
    env: entry.env ? { ...entry.env } : {},
    volumes: entry.volumes ? [...entry.volumes] : [],
    ...(entry.user ? { user: entry.user } : {}),
    ...(entry.healthcheck ? { healthcheck: entry.healthcheck } : {}),
    ...(entry.restart ? { restart: entry.restart } : {}),
    ...(entry.command ? { command: entry.command } : {}),
    ...(entry.connectionEnv
      ? { connectionEnv: { ...entry.connectionEnv } }
      : {}),
  };
}

/** Whether `name` is a known curated catalog service. */
export function isCuratedService(name: string): boolean {
  return Object.prototype.hasOwnProperty.call(SERVICE_CATALOG, name);
}

/**
 * Expand a curated catalog name into a full `ServiceObject` — the
 * init-sugar form written into the yml so the builder sees (and can
 * edit) every field. Env values render as `${KEY}` placeholders (their
 * literal defaults are seeded into `<name>.env` via
 * `curatedServiceEnvDefaults`), so the yml is shareable without baking
 * credentials in. Throws if `name` isn't curated.
 */
export function expandCuratedService(name: string): ServiceObject {
  const def = SERVICE_CATALOG[name];
  if (!def) {
    throw new Error(
      `Unknown service '${name}'. Known catalog services: ${knownServices().join(', ')}.`,
    );
  }
  return {
    name: def.id,
    image: def.image,
    port: def.defaultPort,
    // Visible in the yml, unlike `deferStart`: the builder can drop the line
    // to keep this instance off the LAN and off the proxy.
    ...(def.httpPort !== undefined ? { httpPort: def.httpPort } : {}),
    ...(def.env
      ? {
          env: Object.fromEntries(
            Object.keys(def.env).map((k) => [k, `\${${k}}`]),
          ),
        }
      : {}),
    ...(def.dataMount ? { volumes: [`data:${def.dataMount}`] } : {}),
    ...(def.user ? { user: def.user } : {}),
    ...(def.command ? { command: def.command } : {}),
    ...(def.healthcheck ? { healthcheck: def.healthcheck } : {}),
    // Bake the connection-env templates into the yml (suffix → template) so
    // they travel with the service: a renamed/duplicated instance keeps them,
    // and `serviceConnectionEnv` prefixes by the instance's current name.
    ...(def.connectionEnv ? { connectionEnv: { ...def.connectionEnv } } : {}),
    // No `restart:` default on purpose (issue #19). The workspace container
    // carries no restart policy, so on a Docker/host restart it stays down
    // until `monoceros start`; defaulting services to `unless-stopped` made
    // them auto-start ALONE, without the workbench they belong to. Leaving it
    // off keeps the whole compose group's restart behavior uniform - nothing
    // comes back on its own. `restart` stays a per-service opt-in in the yml
    // (schema.ts) for builders who deliberately want a policy.
  };
}

/**
 * The literal `KEY=<default>` values to seed into `<name>.env` for a
 * curated service's `${KEY}` env placeholders — the same dev-defaults
 * the catalog declares. Empty for services without env (redis).
 * `init` and `add-service` upsert these so the builder gets a working
 * container without filling anything, yet can change a value (e.g. a
 * real password) in one gitignored place. Returns `{}` for non-curated
 * names.
 */
export function curatedServiceEnvDefaults(
  name: string,
): Record<string, string> {
  const def = SERVICE_CATALOG[name];
  return def?.env ? { ...def.env } : {};
}

/**
 * Connection environment variables injected into the WORKSPACE container,
 * **per service instance** (ADR 0021). Each service that carries a
 * `connectionEnv` (suffix → template) emits `<UPPER(name)>_<SUFFIX>` —
 * e.g. a service named `postgres` → `POSTGRES_URL`, `POSTGRES_HOST`, …; a
 * second one named `analytics` → `ANALYTICS_URL`, … Because service names are
 * unique, the var names are unique by construction, so any number of databases
 * (same or different engine) coexist with no collision and one code path.
 *
 * Tokens in each template are filled here: `${host}` = the service's CURRENT
 * name (rename-safe), `${port}` = svc.port, `${<OPTION>}` = the service's
 * already-resolved env value. The templates are read from the service itself
 * (baked into the yml at expand), not looked up by catalog name, so renamed
 * and custom instances work too.
 *
 * Monoceros deliberately does NOT inject bare `DATABASE_URL` / `PGHOST` etc.:
 * those are project/framework concerns. A tool that wants `DATABASE_URL` reads
 * `<NAME>_URL` (the briefing says so) or maps it in the project's `.env`.
 */
export interface ServiceConnectionEnvOptions {
  /** Workbench name, needed for the `<NAME>_PUBLIC_URL` proxy address. */
  containerName?: string;
  /** Traefik host port (`routing.hostPort`); spelled out when not 80. */
  hostPort?: number;
}

export function serviceConnectionEnv(
  services: readonly ResolvedService[],
  opts: ServiceConnectionEnvOptions = {},
): Record<string, string> {
  const env: Record<string, string> = {};
  for (const svc of services) {
    // `<NAME>_PUBLIC_URL`: the address the service answers on OUTSIDE the
    // container, for every service the catalog marks reachable. The agent needs
    // it for the same reason it needs the `.localhost` URL of a port - a
    // redirect URI in a realm, a link shown to a user - and building it from
    // the container name by hand is exactly the guessing the briefing is meant
    // to end. Emitted before the templates below, and independent of them: a
    // service can be reachable without having connection templates at all.
    if (svc.httpPort !== undefined && opts.containerName) {
      const suffix =
        !opts.hostPort || opts.hostPort === 80 ? '' : `:${opts.hostPort}`;
      env[`${envPrefix(svc.name)}_PUBLIC_URL`] =
        `http://${serviceProxyAlias(opts.containerName, svc.name)}.localhost${suffix}`;
    }
    // The templates on the service win: `expand`/`add-service` serialise a
    // `connectionEnv` block into the yml (renderServiceObjectBody), so they
    // travel with the instance and a renamed/duplicated curated service
    // resolves here by its own name. The catalog-by-name lookup is only a
    // fallback for a hand-written curated entry that omits the block (and
    // happens to keep the catalog name).
    const conn = svc.connectionEnv ?? SERVICE_CATALOG[svc.name]?.connectionEnv;
    if (!conn || Object.keys(conn).length === 0) continue;
    const prefix = envPrefix(svc.name);
    const host = svc.name;
    const port = svc.port !== undefined ? String(svc.port) : '';
    const fill = (template: string): string =>
      template.replace(/\$\{([A-Za-z0-9_]+)\}/g, (_, token: string) => {
        if (token === 'host') return host;
        if (token === 'port') return port;
        return svc.env[token] ?? '';
      });
    for (const [suffix, template] of Object.entries(conn)) {
      env[`${prefix}_${suffix}`] = fill(template);
    }
  }
  return env;
}

/** Env-var prefix for a service: its name, uppercased, non-alphanumerics to `_`. */
function envPrefix(serviceName: string): string {
  return serviceName.replace(/[^A-Za-z0-9]+/g, '_').toUpperCase();
}

/**
 * APT packages for the CLI client tools of the curated services present in the
 * container (ADR 0020), e.g. a `postgres` service contributes
 * `postgresql-client` so `psql` is available in the workspace. Looked up by
 * catalog name (a renamed instance doesn't auto-contribute — add the package
 * via `aptPackages` if needed). Deduped + sorted; merged into the workspace's
 * apt-packages feature at scaffold time.
 */
export function serviceClientAptPackages(
  services: readonly ResolvedService[],
): string[] {
  const pkgs = new Set<string>();
  for (const svc of services) {
    for (const pkg of SERVICE_CATALOG[svc.name]?.client?.apt ?? []) {
      pkgs.add(pkg);
    }
  }
  return [...pkgs].sort();
}

/**
 * Global npm packages for the CLI client tools of the curated services present
 * (ADR 0020), e.g. a `mongodb` service contributes `mongosh`. Installed in
 * post-create (guarded, so already-present packages are skipped). Looked up by
 * catalog name (renamed instances don't auto-contribute). Deduped + sorted.
 */
export function serviceClientNpmPackages(
  services: readonly ResolvedService[],
): string[] {
  const pkgs = new Set<string>();
  for (const svc of services) {
    for (const pkg of SERVICE_CATALOG[svc.name]?.client?.npm ?? []) {
      pkgs.add(pkg);
    }
  }
  return [...pkgs].sort();
}

/**
 * Derive a compose service name from an image ref. Takes the last
 * path segment, strips the tag/digest, lowercases and sanitises:
 *   rustfs/rustfs:latest  → rustfs
 *   postgres:16-alpine    → postgres
 *   ghcr.io/foo/bar:1     → bar
 *   ghcr.io:5000/x/app    → app
 */
export function deriveServiceName(image: string): string {
  const lastSegment = image.split('/').pop() ?? image;
  const noTag = lastSegment.split('@')[0]!.split(':')[0]!;
  return noTag.toLowerCase().replace(/[^a-z0-9_-]/g, '-');
}
